import { useAuth } from '@/auth/auth-provider'
import {
  ASSIGNABLE_ROLES,
  AssignableRole,
  DashboardUser,
  DashboardUserList,
  dashboardUsersApi,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
} from '@/services/dashboardUsers.api'
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Pagination,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IconAlertTriangle, IconRefresh, IconUserPlus } from '@tabler/icons-react'
import { useCallback, useEffect, useState } from 'react'

const PER_PAGE = 25

const roleOptions = ASSIGNABLE_ROLES.map(role => ({ value: role, label: ROLE_LABELS[role] }))

function roleBadgeColor(role: string): string {
  return role === 'SUPER_ADMIN' ? 'teal' : 'gray'
}

/** The API's message for a failed request, falling back to the transport error. */
function errorMessage(err: unknown): string {
  const response = (err as { response?: { data?: { message?: unknown } } })?.response
  const message = response?.data?.message
  if (typeof message === 'string') return message
  if (Array.isArray(message)) return message.join(', ')
  return err instanceof Error ? err.message : 'Please try again.'
}

/**
 * Who may use the dashboard. Staff sign in with Entra; a row here grants them a role, picked up on
 * their next request. Super admins only — the API enforces the same.
 */
export function UserManagement() {
  const { user } = useAuth()
  const [list, setList] = useState<DashboardUserList | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<'active' | 'disabled' | null>(null)
  const [page, setPage] = useState(1)
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<DashboardUser | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setError(null)
      setList(
        await dashboardUsersApi.list({
          search: search.trim() || undefined,
          role: roleFilter ?? undefined,
          status: statusFilter ?? undefined,
          page,
          perPage: PER_PAGE,
        }),
      )
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [search, roleFilter, statusFilter, page])

  useEffect(() => {
    const timer = setTimeout(load, 250)
    return () => clearTimeout(timer)
  }, [load])

  const isSelf = (row: DashboardUser) => Boolean(user?.email && row.email.toLowerCase() === user.email.toLowerCase())

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={3}>User Management</Title>
          <Text size="sm" c="dimmed">
            Staff sign in with their ProMax or NCC Microsoft account. Add them here to give them a role.
          </Text>
        </div>
        <Group>
          <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={load} loading={loading}>
            Refresh
          </Button>
          <Button leftSection={<IconUserPlus size={16} />} onClick={() => setAdding(true)}>
            Add user
          </Button>
        </Group>
      </Group>

      <Group align="flex-end">
        <TextInput
          label="Search"
          placeholder="Name or email"
          value={search}
          onChange={event => {
            setSearch(event.currentTarget.value)
            setPage(1)
          }}
          style={{ flex: 1 }}
        />
        <Select
          label="Role"
          placeholder="Any"
          clearable
          data={roleOptions}
          value={roleFilter}
          onChange={value => {
            setRoleFilter(value)
            setPage(1)
          }}
        />
        <Select
          label="Status"
          placeholder="Any"
          clearable
          data={[
            { value: 'active', label: 'Active' },
            { value: 'disabled', label: 'Disabled' },
          ]}
          value={statusFilter}
          onChange={value => {
            setStatusFilter(value as 'active' | 'disabled' | null)
            setPage(1)
          }}
        />
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          {error}
        </Alert>
      )}

      <Card withBorder padding={0} radius="md">
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>User</Table.Th>
              <Table.Th>Role</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th w={100} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading && !list ? (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Group justify="center" py="xl">
                    <Loader size="sm" />
                  </Group>
                </Table.Td>
              </Table.Tr>
            ) : list?.users.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text ta="center" c="dimmed" py="xl">
                    No users match your filters
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              list?.users.map(row => (
                <Table.Tr key={row.id}>
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {row.name ?? row.email}
                      {isSelf(row) && (
                        <Text component="span" size="xs" c="dimmed" fw={500} ml={6}>
                          (you)
                        </Text>
                      )}
                    </Text>
                    {row.name && (
                      <Text size="xs" c="dimmed">
                        {row.email}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="light" color={roleBadgeColor(row.role)}>
                      {ROLE_LABELS[row.role] ?? row.role}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {!row.isActive ? (
                      <Badge size="sm" variant="light" color="red">
                        Disabled
                      </Badge>
                    ) : row.hasSignedIn ? (
                      <Badge size="sm" variant="light" color="green">
                        Active
                      </Badge>
                    ) : (
                      <Badge size="sm" variant="light" color="cyan">
                        Not signed in yet
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Button size="xs" variant="default" disabled={isSelf(row)} onClick={() => setEditing(row)}>
                      Edit
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Card>

      {list && list.total > PER_PAGE && (
        <Group justify="center">
          <Pagination total={Math.ceil(list.total / PER_PAGE)} value={page} onChange={setPage} />
        </Group>
      )}

      <AddUserModal
        opened={adding}
        onClose={() => setAdding(false)}
        onSaved={() => {
          setAdding(false)
          load()
        }}
      />
      <EditUserModal
        user={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          load()
        }}
      />
    </Stack>
  )
}

function AddUserModal({ opened, onClose, onSaved }: { opened: boolean; onClose: () => void; onSaved: () => void }) {
  const [saving, setSaving] = useState(false)
  const form = useForm<{ email: string; name: string; role: AssignableRole }>({
    initialValues: { email: '', name: '', role: 'STAFF' },
    validate: {
      email: value => (/^\S+@\S+\.\S+$/.test(value.trim()) ? null : 'Enter the address they sign in to Microsoft with'),
      name: value => (value.trim() ? null : 'Enter their name'),
    },
  })

  const close = () => {
    form.reset()
    onClose()
  }

  const submit = form.onSubmit(async values => {
    setSaving(true)
    try {
      await dashboardUsersApi.create({ email: values.email.trim(), name: values.name.trim(), role: values.role })
      notifications.show({ title: 'User added', message: 'They can sign in now.', color: 'green' })
      form.reset()
      onSaved()
    } catch (err) {
      notifications.show({ title: 'Could not add user', message: errorMessage(err), color: 'red' })
    } finally {
      setSaving(false)
    }
  })

  return (
    <Modal opened={opened} onClose={close} title="Add user">
      <form onSubmit={submit}>
        <Stack>
          <TextInput label="Email" placeholder="name@promaxunlimited.com" required {...form.getInputProps('email')} />
          <TextInput label="Name" required {...form.getInputProps('name')} />
          <Select
            label="Role"
            data={roleOptions}
            allowDeselect={false}
            description={ROLE_DESCRIPTIONS[form.values.role]}
            {...form.getInputProps('role')}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={close} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" loading={saving}>
              Add user
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}

function EditUserModal({ user, onClose, onSaved }: { user: DashboardUser | null; onClose: () => void; onSaved: () => void }) {
  const [saving, setSaving] = useState(false)
  const [role, setRole] = useState<AssignableRole>('STAFF')
  const [isActive, setIsActive] = useState(true)

  useEffect(() => {
    if (user) {
      setRole((ASSIGNABLE_ROLES as readonly string[]).includes(user.role) ? (user.role as AssignableRole) : 'STAFF')
      setIsActive(user.isActive)
    }
  }, [user])

  if (!user) {
    return null
  }

  const unchanged = role === user.role && isActive === user.isActive

  const save = async () => {
    setSaving(true)
    try {
      await dashboardUsersApi.update(user.id, {
        ...(role !== user.role && { role }),
        ...(isActive !== user.isActive && { isActive }),
      })
      notifications.show({ title: 'User updated', message: 'The change applies on their next request.', color: 'green' })
      onSaved()
    } catch (err) {
      notifications.show({ title: 'Could not update user', message: errorMessage(err), color: 'red' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal opened onClose={onClose} title={`Edit ${user.name ?? user.email}`}>
      <Stack>
        <Text size="sm" c="dimmed">
          {user.email}
        </Text>
        <Select
          label="Role"
          data={roleOptions}
          allowDeselect={false}
          value={role}
          description={ROLE_DESCRIPTIONS[role]}
          onChange={value => value && setRole(value as AssignableRole)}
        />
        <Switch
          label="Active"
          description="Turning this off takes their access away on their next request"
          checked={isActive}
          onChange={event => setIsActive(event.currentTarget.checked)}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} loading={saving} disabled={unchanged}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
